/*
 * Represents a custom element for displaying a country popup.
 *  It is designed to handle the functionality of a country popup on a web page.
 * Code summary:

- The constructor method is called when a new instance of the CountryPopup class is created. It initializes various properties, such as options, country_popup_cookie_identifier, user_country_cookie_identifier, and expirationDate. The options property is parsed from the data-country-message-options attribute of the HTML element.
- The connectedCallback method is called when the element is connected to the DOM. It fetches the user's country code and checks if it is different from the current country code specified in the options. If the country popup cookie is not set, it renders the country popup.
- The checkExpiresInDays method checks if the expires_in_days option is set to 0. If true, it removes the country popup cookie as we want the popup to be shown every time in this instance (test mode).
- The removeCookie method removes a cookie by setting its expiration date to the past. It takes the cookieIdentifier as a parameter.
- The removeCountryCodeCookie method removes the 'country_code' cookie by calling the removeCookie method with the appropriate identifier.
- The checkCountryPopupCookie method checks if the country popup cookie is set. It retrieves the cookie value using the getCookie method and returns true if the cookie exists, and false otherwise.
- The getCookie method retrieves the value of a cookie by its identifier. It splits the document.cookie string into individual cookies, iterates through them, and returns the value of the cookie matching the provided cookieIdentifier.
- The fetchCountryCode method fetches the user's country code either from the 'user_country_code' cookie or from an external location API specified in the options. If the country code is retrieved from the API, it sets the 'user_country_code' cookie with the user's country code and returns the country code.
- The closePopupOnClick method adds a click event listener to the close button element. When the button is clicked, it checks if the Fancybox library is available and closes the popup if it is.
- The renderPopup method renders the country popup based on the user's country code. It retrieves the country information from the options.popup_messages object using the user's country code. If the country information exists, it generates the HTML markup for the popup and creates a new instance of the Fancybox class with the popup HTML. It also sets the country popup cookie.

*/

import { Fancybox } from '@fancyapps/ui';

if (!customElements.get('country-popup')) {
  class CountryPopup extends HTMLElement {
    constructor() {
      super();
      this.options = JSON.parse(this.getAttribute('data-country-message-options'));
      this.country_popup_cookie_identifier = 'country_popup';
      this.user_country_cookie_identifier = 'user_country_code';
      this.expirationDate = new Date();
      if (this.options.expires_in_days) {
        this.expirationDate.setDate(this.expirationDate.getDate() + this.options.expires_in_days);
      }
    }

    connectedCallback() {
      this.fetchCountryCode().then((userCountryCode) => {
        if (
          userCountryCode !== this.options.current_country_code &&
          !this.checkCountryPopupCookie()
        ) {
          this.renderPopup(userCountryCode);
        }
      });
    }

    checkExpiresInDays() {
      if (this.options.expires_in_days === 0) {
        this.removeCookie(this.country_popup_cookie_identifier);
      }
    }

    removeCookie(cookieIdentifier) {
      document.cookie = `${cookieIdentifier}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }

    removeCountryCodeCookie() {
      this.removeCookie('country_code');
    }

    checkCountryPopupCookie() {
      const countryPopupCookie = this.getCookie(this.country_popup_cookie_identifier);
      return !!countryPopupCookie;
    }

    getCookie(cookieIdentifier) {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith(cookieIdentifier + '=')) {
          return trimmedCookie.substring(cookieIdentifier.length + 1);
        }
      }
      return null;
    }

    async fetchCountryCode() {
      const countryCodeCookie = this.getCookie(this.user_country_cookie_identifier);
      if (countryCodeCookie) {
        return countryCodeCookie;
      } else {
        const response = await fetch(this.options.location_api_url);
        const data = await response.json();
        const countryCode = data.countryCode;
        document.cookie = `${
          this.user_country_cookie_identifier
        }=${countryCode}; expires=${this.expirationDate.toUTCString()}; path=/;`;
        return data.countryCode;
      }
    }

    closePopupOnClick(closeButton) {
      closeButton?.addEventListener('click', () => {
        Fancybox?.close();
      });
    }

    renderPopup(userCountryCode) {
      const countryInfo = this.options.popup_messages[userCountryCode];
      if (!countryInfo) return;
      if (Shopify.designMode) return;

      const popupHTML = `
      <div class="country-popup text-center  max-w-[500px]">
          <div class="country-popup__wrapper flex flex-col gap-4">
            <h2>
              <span>${countryInfo.country_title}</span>
              <span> ${countryInfo.country_name}</span>
            </h2>
            <p>${countryInfo.country_body}</p>
            <div class="country-popup__button-wrapper flex flex-col gap-4 mt-2">
              <a class="button button--primary" href="${countryInfo.country_url}">${countryInfo.country_button}</a>
              <button class="button button--outline" id="CountryPopupClose">${countryInfo.country_stay}</button>
              </div>
          </div>
        </div>
    `;

      new Fancybox(
        [
          {
            src: popupHTML,
            type: 'html',
          },
        ],
        {
          on: {
            done: (fancybox) => {
              const closeButton = document.getElementById('CountryPopupClose');
              this.closePopupOnClick(closeButton);
            },
          },
        }
      );

      document.cookie = `${
        this.country_popup_cookie_identifier
      }=true; expires=${this.expirationDate.toUTCString()}; path=/`;
    }
  }

  customElements.define('country-popup', CountryPopup);
}
